import React, { useEffect } from 'react';
import { Collapse } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';

import i18n from 'I18n';
import config from 'config';

import { listItem, titleForItem } from 'components/banners/DisplayItems';
import CustomDateTimeSelect from 'components/base/CustomDateTimeSelect';
import CustomSwitchButton from 'components/base/CustomSwitchButton';
import CustomTitleWithInput from 'components/base/CustomTitleWithInput';
import {
  ReactHookFormErrorMessage,
  errorMessage,
  hasError,
} from 'components/base/ErrorFieldMessage';
import { ReactHookFormImageUploader } from 'components/base/ImageUploader';
import InputFieldControl from 'components/base/InputFieldControl';
import CampaignSelectDropdown from 'components/message/CampaignSelectDropdown';
import { BANNER_CONTENT_TYPE, LanguageConfig } from 'config/CustomEnums';

import { compareDate, getReadablePeriod } from 'utils/TimeFormatUtil';

export const ContentType = {
  Campaign: 'Campaign',
  Product: 'Product',
};

const CreateBannerContent = ({ language }) => {
  const { watch, setValue, formState } = useFormContext();
  const { errors } = formState;
  const watchContent = watch(`content`);
  const watchBannerType = watch(`contentType`);
  const watchCoverPhotos = watch(`translations.${language}.coverPhotos.0`);
  const watchCampaign = watch(`campaign`);
  const watchFollowCampaign = watch(`visiblePeriodFollowLinkedCampaign`);
  const watchVisiblePeriodStartDate = watch(`visiblePeriodStartDate`);
  const watchVisiblePeriodEndDate = watch(`visiblePeriodEndDate`);
  const watchOrder = watch(`order`);

  console.log('@@49: ', errors, watchCampaign, watchCoverPhotos);

  if (language !== LanguageConfig.english) {
    return (
      <>
        <div className="first-section-title">
          {i18n.t('content', { locale: language })}
        </div>
        <ReactHookFormImageUploader
          title={'Banner image'}
          uploadSuggest={i18n.t('square_cover_photo_suggest_with_variable', {
            locale: language,
            ...config.bannerSize,
            minWidth: 900,
          })}
          name={`translations.${language}.coverPhotos.0`}
          minWidth={900}
          minHeight={config.bannerSize.minHeight}
          value={watchCoverPhotos}
          aspect={900 / config.bannerSize.minHeight}
          maxImageNum={1}
          language={language}
        />
      </>
    );
  }

  return (
    <>
      <div className="first-section-title">
        {i18n.t('content', { locale: language })}
      </div>
      {watchBannerType === BANNER_CONTENT_TYPE.CAMPAIGN ? (
        <>
          <CampaignSelectDropdown
            title="Related campaign"
            isPrompt
            defaultValue={{
              value: watchCampaign,
              label: watchCampaign?.pk
                ? `[ID: ${watchCampaign?.pk}] ${watchCampaign?.name}`
                : watchCampaign?.name,
            }}
            setValue={(value) => {
              setValue('campaign', value, { shouldDirty: true });
            }}
          />
          <ReactHookFormErrorMessage errors={errors} id="campaign" />
          {listItem('Campaign name', watchCampaign?.name)}
          {listItem('Campaign ID', watchCampaign?.pk)}
          {listItem(
            'Campaign’s active period',
            getReadablePeriod(watchCampaign?.startDate, watchCampaign?.endDate),
          )}
          {listItem(
            'Campaign’s visible period',
            getReadablePeriod(
              watchCampaign?.displayStartDate,
              watchCampaign?.displayEndDate,
            ),
          )}
        </>
      ) : null}
      {titleForItem('Banner visible period')}
      {watchBannerType === BANNER_CONTENT_TYPE.CAMPAIGN ? (
        <>
          <CustomSwitchButton
            disabled={false}
            defaultChecked={watchFollowCampaign}
            onChange={(value) => {
              setValue('visiblePeriodFollowLinkedCampaign', value, {
                shouldDirty: true,
              });
            }}
          />
          {!!watchFollowCampaign ? (
            <label className="section-short-description">
              This setting is following the setting from the linked campaign, for further edit, go to the related campaign to edit.
            </label>
          ) : null}
        </>
      ) : null}
      <Collapse
        in={
          watchBannerType === BANNER_CONTENT_TYPE.CAMPAIGN &&
          !watchFollowCampaign
        }
      >
        <div>
          <ReactHookFormErrorMessage errors={errors} id={'visiblePeriod'} />
          <div>
            <label className="create-section-label create-section-label-bottom-space">
              {'Start date'}
            </label>
            <CustomDateTimeSelect
              disabled={watchFollowCampaign}
              defaultTime={watchVisiblePeriodStartDate}
              minDate={
                watchCampaign?.displayStartDate
                  ? new Date(watchCampaign.displayStartDate)
                  : null
              }
              maxDate={
                watchCampaign?.displayEndDate
                  ? new Date(watchCampaign.displayEndDate)
                  : null
              }
              error={hasError(errors, 'visiblePeriodStartDate')}
              onTimeChange={(date) => {
                setValue('visiblePeriodStartDate', date, { shouldDirty: true });
              }}
            />
            {/* <ReactHookFormErrorMessage
              errors={errors}
              id={'visiblePeriodStartDate'}
            /> */}
          </div>
          <div>
            <label className="create-section-label create-section-label-bottom-space">
              {'End date'}
            </label>
            <CustomDateTimeSelect
              disabled={watchFollowCampaign}
              defaultTime={watchVisiblePeriodEndDate}
              minDate={
                watchCampaign?.displayStartDate
                  ? new Date(watchCampaign.displayStartDate)
                  : null
              }
              maxDate={
                watchCampaign?.displayEndDate
                  ? new Date(watchCampaign.displayEndDate)
                  : null
              }
              error={hasError(errors, 'visiblePeriodEndDate')}
              onTimeChange={(date) => {
                setValue('visiblePeriodEndDate', date, { shouldDirty: true });
              }}
            />
            {/* <ReactHookFormErrorMessage
              errors={errors}
              id={'visiblePeriodEndDate'}
            /> */}
          </div>
        </div>
      </Collapse>
      <ReactHookFormErrorMessage errors={errors} id={`publishLocation`} />

      <ReactHookFormImageUploader
        title={'Banner image'}
        uploadSuggest={i18n.t('square_cover_photo_suggest_with_variable', {
          locale: language,
          ...config.bannerSize,
          minWidth: 900,
        })}
        name={`translations.${language}.coverPhotos.0`}
        minWidth={900}
        minHeight={config.bannerSize.minHeight}
        value={watchCoverPhotos}
        aspect={900 / config.bannerSize.minHeight}
        maxImageNum={1}
        language={language}
      />

      <CustomTitleWithInput
        title={'Display order'}
        error={{ error: hasError(errors, 'order') }}
        defaultValue={watchOrder}
        setValue={(value) => {
          setValue('order', value, { shouldDirty: true });
        }}
        type={'number'}
        extra={{
          min: 1,
        }}
      />
      <ReactHookFormErrorMessage errors={errors} id="order" />
      <label className="create-message-suggest">
        {`Minimum value is 1. The smaller value, the higher priority.`}
      </label>
    </>
  );
};

export default CreateBannerContent;
